import React, { useState } from "react";
import { Trash2, AlertTriangle, Loader2, Eye, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

/**
 * Reset Production : permet de vider sélectivement les collections opérationnelles
 * (lots, ventes, commandes B2C, sessions de réassort, etc.) avant la mise en
 * production réelle. Les collections de RÉFÉRENCE sont toujours préservées
 * (produits, distributeurs, ingrédients, fournisseurs, utilisateurs, paramètres).
 *
 * UX :
 *   1. Coche les catégories à vider
 *   2. Bouton "Aperçu (dry-run)" → affiche le nombre de documents qui seraient supprimés
 *   3. Bouton "Confirmer la suppression" (rouge, double confirmation) → exécute la suppression
 */
const SCOPES = [
  { key: "lots",              label: "Lots de production",                desc: "Toutes les barquettes et leur traçabilité", danger: true },
  { key: "movements",         label: "Mouvements de stock",                desc: "Entrées, sorties, transferts, ajustements", danger: true },
  { key: "sales",             label: "Ventes",                             desc: "Ventes manuelles, Nayax, réassort terrain", danger: true },
  { key: "restock_sessions",  label: "Sessions de réassort",               desc: "Historique des tournées terrain" },
  { key: "production_orders", label: "Ordres de fabrication",              desc: "OF planifiés ou réalisés" },
  { key: "shop_orders",       label: "Commandes boutique B2C",             desc: "+ paiements associés", danger: true },
  { key: "delivery_notes",    label: "Bons de livraison" },
  { key: "invoices",          label: "Factures" },
  { key: "inventories",       label: "Inventaires" },
  { key: "print_history",     label: "Historique d'impression",            desc: "Étiquettes + PDFs revendeurs" },
  { key: "temperature_logs",  label: "Relevés de température" },
  { key: "lab_tests",         label: "Tests labo" },
  { key: "login_attempts",    label: "Tentatives de connexion (logs)",     desc: "Brute-force tracking admin + boutique" },
  { key: "shop_customers",    label: "Clients boutique B2C",               desc: "⚠️ Préserver si déjà en prod" },
];

const QUICK_PRESETS = {
  pre_prod: ["lots", "movements", "sales", "restock_sessions", "production_orders", "shop_orders", "delivery_notes", "invoices", "inventories", "print_history", "temperature_logs", "lab_tests", "login_attempts"],
  only_operations: ["lots", "movements", "sales", "restock_sessions", "production_orders", "inventories"],
  only_shop: ["shop_orders", "shop_customers"],
};

export default function ResetProductionDialog() {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [preview, setPreview] = useState(null); // { results, total }
  const [previewing, setPreviewing] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const toggle = (key) => {
    setPreview(null);
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });
  };

  const applyPreset = (key) => {
    setPreview(null);
    setSelected(new Set(QUICK_PRESETS[key]));
  };

  const reset = () => {
    setSelected(new Set());
    setPreview(null);
  };

  const runDryRun = async () => {
    if (selected.size === 0) {
      toast.error("Sélectionnez au moins une catégorie");
      return;
    }
    setPreviewing(true);
    try {
      const r = await api.post("/admin/reset-data", {
        scopes: Array.from(selected),
        dry_run: true,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'aperçu");
    } finally {
      setPreviewing(false);
    }
  };

  const runExecute = async () => {
    setExecuting(true);
    try {
      const r = await api.post("/admin/reset-data", {
        scopes: Array.from(selected),
        dry_run: false,
      });
      toast.success(`${r.data.total_documents} document(s) supprimé(s) avec succès`);
      setPreview(null);
      setSelected(new Set());
      setConfirmOpen(false);
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression");
    } finally {
      setExecuting(false);
    }
  };

  return (
    <>
      <Button
        variant="destructive"
        onClick={() => setOpen(true)}
        data-testid="reset-production-btn"
      >
        <Trash2 className="w-4 h-4 mr-2" />
        Reset pour mise en production
      </Button>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="max-w-3xl" data-testid="reset-production-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Reset des données opérationnelles
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm pt-1">
                <p>
                  Cet outil permet de <strong>vider sélectivement</strong> les collections opérationnelles
                  avant une mise en production. Le référentiel (produits, distributeurs, ingrédients,
                  fournisseurs, utilisateurs, paramètres, pages CMS) est <strong>toujours préservé</strong>.
                </p>
                <div className="flex gap-2 flex-wrap">
                  <Button type="button" variant="outline" size="sm" onClick={() => applyPreset("pre_prod")} data-testid="preset-pre-prod">
                    Préréglage : pré-production (tout sauf clients boutique)
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyPreset("only_operations")} data-testid="preset-operations">
                    Seulement opérations
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => applyPreset("only_shop")} data-testid="preset-shop">
                    Seulement boutique
                  </Button>
                  <Button type="button" variant="ghost" size="sm" onClick={reset} data-testid="preset-reset">
                    Tout désélectionner
                  </Button>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-[50vh] overflow-y-auto border border-[#E5E5E0] rounded p-3 space-y-1.5">
            {SCOPES.map((s) => {
              const isSel = selected.has(s.key);
              const previewCount = preview?.results?.[s.key]
                ? Object.values(preview.results[s.key]).reduce((a, b) => a + b, 0)
                : null;
              return (
                <label
                  key={s.key}
                  className={`flex items-start gap-3 px-3 py-2 rounded cursor-pointer transition-colors ${
                    isSel ? "bg-[#FBEEEA] border border-[#E8B0A0]" : "hover:bg-[#F4F4F0] border border-transparent"
                  }`}
                  data-testid={`scope-row-${s.key}`}
                >
                  <Checkbox
                    checked={isSel}
                    onCheckedChange={() => toggle(s.key)}
                    className="mt-0.5"
                    data-testid={`scope-${s.key}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-[#1A1C1A]">{s.label}</span>
                      {s.danger && (
                        <span className="text-[10px] uppercase tracking-wider bg-[#FBEEEA] text-[#A04E2C] border border-[#E8B0A0] px-1.5 py-0.5 rounded">
                          critique
                        </span>
                      )}
                    </div>
                    {s.desc && <p className="text-xs text-[#575A57]">{s.desc}</p>}
                  </div>
                  {previewCount !== null && (
                    <div className="text-right flex-shrink-0">
                      <div className="text-[10px] uppercase tracking-wider text-[#A04E2C]">Sera supprimé</div>
                      <div className="text-sm font-mono font-semibold text-[#A04E2C]" data-testid={`count-${s.key}`}>
                        {previewCount}
                      </div>
                    </div>
                  )}
                </label>
              );
            })}
          </div>

          {preview && (
            <div className="bg-[#FBEEEA] border border-[#E8B0A0] rounded p-3 flex items-center gap-2 text-sm" data-testid="reset-preview-summary">
              <AlertTriangle className="w-4 h-4 text-[#A04E2C] flex-shrink-0" />
              <span className="text-[#A04E2C]">
                <strong>{preview.total_documents}</strong> document(s) seraient supprimés dans
                <strong> {preview.scopes_processed.length} catégorie(s)</strong>.
              </span>
            </div>
          )}

          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel data-testid="reset-prod-cancel">Annuler</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={runDryRun}
              disabled={previewing || selected.size === 0}
              data-testid="reset-prod-dryrun-btn"
            >
              {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Eye className="w-4 h-4 mr-2" />}
              Aperçu (dry-run)
            </Button>
            <Button
              variant="destructive"
              onClick={() => setConfirmOpen(true)}
              disabled={!preview || preview.total_documents === 0}
              data-testid="reset-prod-execute-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer définitivement
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="reset-final-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-700 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Confirmation finale
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm pt-1">
                <p>
                  Vous êtes sur le point de supprimer définitivement
                  <strong className="text-red-700"> {preview?.total_documents || 0} document(s) </strong>
                  dans {preview?.scopes_processed?.length || 0} catégorie(s).
                </p>
                <p className="text-red-700 font-semibold">Cette action est IRRÉVERSIBLE.</p>
                <p className="text-xs text-[#575A57]">
                  Pensez à exporter une sauvegarde JSON avant si nécessaire (Paramètres → Export).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="final-confirm-cancel">Non, annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={runExecute}
              disabled={executing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="final-confirm-execute"
            >
              {executing ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Suppression…</>
              ) : (
                <><CheckCircle2 className="w-4 h-4 mr-2" /> Oui, supprimer</>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
